import { apiRequest, ApiResponse } from '../request'
import { AccessibleCompany } from '../common/interfaces/user.interface'
import { Company, Subscription } from '../common/interfaces/company.interface'

export interface Organization {
    id: number
    name: string
    tradingName?: string
    /**
     * Natureza do titular. Decide o documento exigido, o KYC da conta digital
     * e se o módulo fiscal existe. MEI é JURIDICA — tem CNPJ e emite nota.
     */
    personType?: 'FISICA' | 'JURIDICA'
    cnpj?: string
    /** Preenchido quando `personType = FISICA`; nesse caso `cnpj` fica vazio. */
    cpf?: string
    email?: string
    phone?: string
    website?: string
    logoUrl?: string
    active: boolean
    /**
     * Assistente de primeira entrada. `onboardingCompletedAt` nulo numa conta de
     * saúde = o assistente ainda vai abrir (ver `RegistrationGuard`).
     */
    onboardingProfile?: 'CLINIC' | 'INDIVIDUAL_DOCTOR' | null
    onboardingCompletedAt?: string | null
    onboardingAnswers?: Record<string, unknown> | null
    createdAt: string
    updatedAt: string
    companies?: Company[]
    subscription?: Subscription | null
    _count?: {
        companies: number
        userCompanies: number
    }
}

export interface CreateOrganizationDto {
    name: string
    tradingName?: string
    cnpj?: string
    email?: string
    phone?: string
    website?: string
    logoUrl?: string
    active?: boolean
}

export interface UpdateOrganizationDto extends Partial<CreateOrganizationDto> {}

export interface OrganizationListResponse {
    data: Organization[]
    total: number
    page: number
    limit: number
    totalPages: number
}

export interface SwitchCompanyResponse {
    access_token: string
    refresh_token: string
    company: Company
    planModules?: Record<string, string[]>
}

// Organization CRUD
export async function getOrganizations(
    page = 1,
    limit = 20,
    search?: string
): Promise<ApiResponse<OrganizationListResponse>> {
    const term = search?.trim()
    const query = term ? `&search=${encodeURIComponent(term)}` : ''
    return apiRequest<OrganizationListResponse>(
        `organizations?page=${page}&limit=${limit}${query}`,
        {},
        'get'
    )
}

export async function getOrganization(id: number): Promise<ApiResponse<Organization>> {
    return apiRequest<Organization>(`organizations/${id}`, {}, 'get')
}

export async function createOrganization(
    data: CreateOrganizationDto
): Promise<ApiResponse<Organization>> {
    return apiRequest<Organization>('organizations', { data }, 'post')
}

export async function updateOrganization(
    id: number,
    data: UpdateOrganizationDto
): Promise<ApiResponse<Organization>> {
    return apiRequest<Organization>(`organizations/${id}`, { data }, 'patch')
}

export async function deleteOrganization(id: number): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(`organizations/${id}`, {}, 'delete')
}

export async function addCompanyToOrganization(
    organizationId: number,
    companyId: number
): Promise<ApiResponse<Company>> {
    return apiRequest<Company>(
        `organizations/${organizationId}/companies/${companyId}`,
        {},
        'post'
    )
}

export async function removeCompanyFromOrganization(
    organizationId: number,
    companyId: number
): Promise<ApiResponse<Company>> {
    return apiRequest<Company>(
        `organizations/${organizationId}/companies/${companyId}`,
        {},
        'delete'
    )
}

// Company soft delete
export async function deleteCompany(companyId: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`companies/${companyId}`, {}, 'delete')
}

// Auth - Company switching
export async function getAccessibleCompanies(): Promise<ApiResponse<AccessibleCompany[]>> {
    return apiRequest<AccessibleCompany[]>('auth/accessible-companies', {}, 'get')
}

export async function switchCompany(companyId: number): Promise<ApiResponse<SwitchCompanyResponse>> {
    return apiRequest<SwitchCompanyResponse>(
        'auth/switch-company',
        { data: { companyId } },
        'post'
    )
}

// Subscriptions
export async function updateSubscription(
    subscriptionId: number,
    data: { planId?: number; dueDay?: number; status?: string }
): Promise<ApiResponse<Subscription>> {
    return apiRequest<Subscription>(
        `subscriptions/${subscriptionId}`,
        { data },
        'patch'
    )
}

export async function uploadOrganizationLogo(
    organizationId: number,
    file: File
): Promise<ApiResponse<Organization>> {
    const formData = new FormData()
    formData.append('logo', file)
    return apiRequest<Organization>(
        `organizations/${organizationId}/logo`,
        { data: formData, formData: true },
        'post'
    )
}
